/**
 * Wörter und Regeln des Feedbacks (HM-169, ADR 0047) — für das Formular aus
 * dem Kontomenü und die Seite des Plattform-Admins.
 *
 * **Was mitgesendet wird, steht hier nicht.** Das bestimmt der Server aus
 * Sitzung und Browserkennung, und das Formular zeigt es aus
 * `GET /feedback/context`; eine Liste hier wäre eine zweite Wahrheit.
 */
import type { BadgeTone } from "@hausmeter/ui";
import type { FeedbackKindFilter, FeedbackStatusFilter } from "./router.js";

export const FEEDBACK_KIND_LABELS: Record<FeedbackKindFilter, string> = { bug: "Fehler", wish: "Wunsch" };

/** Die Wahl im Formular — ein Verb, weil sie sagt, was man tut. */
export const FEEDBACK_KIND_ACTIONS: Record<FeedbackKindFilter, string> = {
  bug: "Fehler melden",
  wish: "Wunsch äußern",
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatusFilter, string> = {
  open: "offen",
  accepted: "angenommen",
  done: "umgesetzt",
  rejected: "abgelehnt",
};

export const FEEDBACK_STATUS_TONES: Record<FeedbackStatusFilter, BadgeTone> = {
  open: "info",
  accepted: "warning",
  done: "success",
  rejected: "neutral",
};

/** Die Entscheidungen des Admins (#76); welche gerade geht, sagt der Server in `decisions`. */
export type FeedbackDecision = "accept" | "reject" | "reopen";

/**
 * Wie eine Entscheidung am Knopf heißt. „Wieder öffnen" heißt an einer
 * angenommenen Meldung „Annahme zurücknehmen" — wieder geöffnet wird dort
 * nichts, sie war nie zu.
 */
export function feedbackDecisionLabel(decision: FeedbackDecision, status: FeedbackStatusFilter): string {
  switch (decision) {
    case "accept":
      return "Annehmen";
    case "reject":
      return "Ablehnen …";
    case "reopen":
      return status === "accepted" ? "Annahme zurücknehmen" : "Wieder öffnen";
  }
}

/**
 * Der Satz zum Stand der Übernahme nach Gitea (#76) — oder `null`, wo es
 * keinen gibt. Er sagt, wer gerade am Zug ist: der Abgleich, Gitea oder
 * niemand. **Ohne Frist**: Ob und wie oft der Abgleich läuft, weiß die
 * Oberfläche nicht — auf Staging läuft er gar nicht.
 */
export function feedbackSyncText(item: {
  readonly status: FeedbackStatusFilter;
  readonly awaitsIssue: boolean;
  readonly issue: { readonly number: number } | null;
}): string | null {
  if (item.awaitsIssue) return "Angenommen. Das Issue in Gitea legt der nächste Abgleich an.";
  if (item.issue === null) return null;
  return item.status === "done"
    ? `Umgesetzt: #${item.issue.number} ist in Gitea geschlossen.`
    : `Übernommen als #${item.issue.number}. Den Stand führt jetzt Gitea; ist das Issue geschlossen, steht die Meldung hier als umgesetzt.`;
}

/** Die Platzhalter je Art — sie sagen, was eine brauchbare Meldung enthält. */
export const FEEDBACK_HINTS: Record<FeedbackKindFilter, { readonly title: string; readonly body: string }> = {
  bug: {
    title: "z. B. Tagessumme fehlt nach Zählerwechsel",
    body: "Was hast du getan, was ist passiert, und was hättest du erwartet?",
  },
  wish: {
    title: "z. B. Monatsvergleich mit dem Vorjahr",
    body: "Was möchtest du tun können, und wofür brauchst du es?",
  },
};

/** Grenzen wie im Vertrag (`FEEDBACK_TITLE_MAX`, `FEEDBACK_BODY_MAX`); `feedback.test.ts` hält sie dagegen. */
export const FEEDBACK_TITLE_MAX = 120;
export const FEEDBACK_BODY_MAX = 5000;

/** Senden geht erst, wenn Titel und Beschreibung mehr als Leerraum enthalten — wie der Vertrag prüft. */
export function feedbackReady(title: string, body: string): boolean {
  const t = title.trim();
  const b = body.trim();
  return t.length > 0 && t.length <= FEEDBACK_TITLE_MAX && b.length > 0 && b.length <= FEEDBACK_BODY_MAX;
}

/**
 * Was die Detailseite zur Anlage sagt. **Abgewählt wird genannt, nicht
 * weggelassen** — sonst sähe eine Meldung ohne Anlage aus wie eine, bei der
 * etwas fehlt.
 */
export function feedbackSiteText(site: { readonly included: boolean; readonly site: { readonly name: string } | null }): string {
  if (!site.included) return "vom Absender abgewählt";
  return site.site?.name ?? "inzwischen gelöscht";
}

/** Ob die Liste unter einem Filter steht — für den Satz, wenn nichts passt. */
export function feedbackFiltered(kind: FeedbackKindFilter | null, status: FeedbackStatusFilter | null): boolean {
  return kind !== null || status !== null;
}
